import EGridLineDirection from 'enums/EGridLineDirection';
export default class GridLine {
  private start: number;
  private stop: number;
  private gridSteps: number;
  public constructor(start: number, stop: number, gridSteps: number) {
    if (gridSteps < 1) {
      throw new Error(`Invalid number of grid steps: ${gridSteps}`);
    }

    this.start = start;
    this.stop = stop;
    this.gridSteps = gridSteps;
  }

  public GetGridSteps(): number {
    return this.gridSteps;
  }

  public GetStepAt(step: number): number {
    if (this.start === this.stop) {
      return this.start;
    }
    // Could return start or stop. Probably shouldn't be used in this way.
    if (this.gridSteps == 1) {
      return this.start;
    }
    if (step <= 0) {
      return this.start;
    }
    if (step >= this.gridSteps - 1) {
      return this.stop;
    }
    // compute the size of each step
    const gridStepSize = (this.stop - this.start) / (this.gridSteps - 1);
    const stepValue: number = this.start + step * gridStepSize;
    return parseFloat(stepValue.toFixed(8));
  }

  public GetStep(value: number): number {
    if (this.start === this.stop) {
      return this.start;
    }

    // Could return start or stop. Probably shouldn't be used in this way.
    if (this.gridSteps == 1) {
      return this.start;
    }

    // Check to see if value is outside start/stop range.

    if (this.start < this.stop) {
      if (value <= this.start) {
        return this.start;
      }

      if (value >= this.stop) {
        return this.stop;
      }
    } else {
      if (value <= this.stop) {
        return this.stop;
      }

      if (value >= this.start) {
        return this.start;
      }
    }

    // Compute the distance between grid points.
    const gridStepSize: number = (this.stop - this.start) / (this.gridSteps - 1);

    // Compute the number of steps between value and start. The cast
    // to an int will cause a rounding towards zero.
    // see: C#LS: 6.2.1 Explicit numeric conversions
    const numSteps = parseInt(((value - this.start) / gridStepSize).toString());

    // Find the step closest to value.
    const startStep: number = this.start + numSteps * gridStepSize;

    // Find the next step above the start step.
    const stopStep = startStep + gridStepSize;

    // Find the difference between the value and the start step.
    const startStepDiff = Math.abs(startStep - value);

    // Find the difference between the value and the stop step.
    const stopStepDiff = Math.abs(stopStep - value);

    // If value is closer to the startStep, return that.
    // If the value is closer to the stopStep, return that.
    const closerStep = startStepDiff < stopStepDiff ? startStep : stopStep;

    return parseFloat(closerStep.toFixed(8));
  }
  public GetRandomStep(): number {
    return this.GetStep(this.start + Math.random() * (this.stop - this.start));
  }

  public GetDirection(gridSteps): EGridLineDirection {
    let direction: EGridLineDirection = EGridLineDirection.Uniform;
    for (let i = 1; i < gridSteps.Count; i++) {
      // Check each pair of steps.
      const startStep = gridSteps[i - 1];
      const stopStep = gridSteps[i];

      if (startStep == stopStep) {
        // If two steps are equal continue in the same direction.
        continue;
      } else if (startStep < stopStep) {
        // The line between these two steps is increasing.
        switch (direction) {
          case EGridLineDirection.Uniform:
            // If the steps were uniform, mark them now as increasing.
            direction = EGridLineDirection.Increasing;
            continue;
          case EGridLineDirection.Decreasing:
            // If the steps were decreasing, then return random.
            return EGridLineDirection.Random;
        }
      } else if (startStep > stopStep) {
        // The line between these two steps is decreasing.
        switch (direction) {
          case EGridLineDirection.Uniform:
            // If the steps were uniform, mark them as now increasing.
            direction = EGridLineDirection.Decreasing;
            continue;
          case EGridLineDirection.Increasing:
            // If the steps were increasing, then return random.
            return EGridLineDirection.Random;
        }
      }
    }
    return direction;
  }
}
